import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import QueryKeys from '@constants/queryKeys';
import { MINUTE } from '@constants/various';
import {
  getWorkerDirectMessages,
  getWorkerJobMessages,
  getWorkerInboxIndicators,
} from '@worker/services/inbox/api';
import {
  markChatMessagesAsRead,
  markChatMessagesAsUnread,
} from '@services/chat/api';
import {
  markDirectChatAsRead,
  markDirectChatAsUnread,
} from '@services/directChat/api';
import type {
  JobChatResponse,
  DirectChatsResponse,
} from '@th-types/inbox-reviews.type';

export const useWorkerDirectMessages = (
  offset: number,
  max: number,
  searchText?: string,
  dateRange?: { startDate: Date | undefined; endDate: Date | undefined },
  isUnread?: boolean,
  mode?: string
) => {
  return useQuery({
    queryKey: [
      QueryKeys.WorkerInbox.DIRECT_CHAT,
      offset,
      max,
      searchText,
      dateRange,
      isUnread,
      mode,
    ],
    queryFn: () =>
      getWorkerDirectMessages(
        offset,
        max,
        searchText,
        dateRange,
        isUnread,
        mode
      ),
    staleTime: MINUTE,
  });
};

export const useWorkerJobMessages = (
  offset: number,
  max: number,
  searchText?: string,
  dateRange?: { startDate: Date | undefined; endDate: Date | undefined },
  isUnread?: boolean,
  mode?: string
) => {
  return useQuery({
    queryKey: [
      QueryKeys.WorkerInbox.JOB_CHAT,
      offset,
      max,
      searchText,
      dateRange,
      isUnread,
      mode,
    ],
    queryFn: () =>
      getWorkerJobMessages(offset, max, searchText, dateRange, isUnread, mode),
    staleTime: MINUTE,
  });
};

/**
 * Single fetch that backs all four inbox tab badges. Prefer this over per-tab
 * count hooks at the parent level so we don't pay for one network call per
 * badge.
 */
export const useWorkerInboxIndicators = () => {
  return useQuery({
    queryKey: [QueryKeys.WorkerInbox.INDICATORS],
    queryFn: getWorkerInboxIndicators,
    staleTime: MINUTE,
  });
};

// ─── Chat Read/Unread Mutations ────────────────────────────────────────────

/**
 * Optimistically update unreadMessages count in a job messages query cache entry.
 */
export const useMarkJobChatAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: number) => markChatMessagesAsRead(chatId),
    onMutate: async (chatId: number) => {
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.WorkerInbox.JOB_CHAT],
      });

      const previousQueries = queryClient.getQueriesData<JobChatResponse>({
        queryKey: [QueryKeys.WorkerInbox.JOB_CHAT],
      });

      queryClient.setQueriesData<JobChatResponse>(
        { queryKey: [QueryKeys.WorkerInbox.JOB_CHAT] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            jobChats: old.jobChats.map((chat) =>
              chat.chatId === chatId ? { ...chat, unreadMessages: 0 } : chat
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _chatId, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WorkerInbox.JOB_CHAT],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WorkerInbox.INDICATORS],
      });
    },
  });
};

export const useMarkJobChatAsUnread = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: number) => markChatMessagesAsUnread(chatId),
    onMutate: async (chatId: number) => {
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.WorkerInbox.JOB_CHAT],
      });

      const previousQueries = queryClient.getQueriesData<JobChatResponse>({
        queryKey: [QueryKeys.WorkerInbox.JOB_CHAT],
      });

      queryClient.setQueriesData<JobChatResponse>(
        { queryKey: [QueryKeys.WorkerInbox.JOB_CHAT] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            jobChats: old.jobChats.map((chat) =>
              chat.chatId === chatId
                ? { ...chat, unreadMessages: Math.max(chat.unreadMessages, 1) }
                : chat
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _chatId, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WorkerInbox.JOB_CHAT],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WorkerInbox.INDICATORS],
      });
    },
  });
};

export const useMarkDirectChatAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: number) => markDirectChatAsRead(chatId),
    onMutate: async (chatId: number) => {
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.WorkerInbox.DIRECT_CHAT],
      });

      const previousQueries = queryClient.getQueriesData<DirectChatsResponse>({
        queryKey: [QueryKeys.WorkerInbox.DIRECT_CHAT],
      });

      queryClient.setQueriesData<DirectChatsResponse>(
        { queryKey: [QueryKeys.WorkerInbox.DIRECT_CHAT] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            directChats: old.directChats.map((chat) =>
              chat.chatId === chatId ? { ...chat, unreadMessages: 0 } : chat
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _chatId, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WorkerInbox.DIRECT_CHAT],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WorkerInbox.INDICATORS],
      });
    },
  });
};

export const useMarkDirectChatAsUnread = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: number) => markDirectChatAsUnread(chatId),
    onMutate: async (chatId: number) => {
      await queryClient.cancelQueries({
        queryKey: [QueryKeys.WorkerInbox.DIRECT_CHAT],
      });

      const previousQueries = queryClient.getQueriesData<DirectChatsResponse>({
        queryKey: [QueryKeys.WorkerInbox.DIRECT_CHAT],
      });

      queryClient.setQueriesData<DirectChatsResponse>(
        { queryKey: [QueryKeys.WorkerInbox.DIRECT_CHAT] },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            directChats: old.directChats.map((chat) =>
              chat.chatId === chatId
                ? { ...chat, unreadMessages: Math.max(chat.unreadMessages, 1) }
                : chat
            ),
          };
        }
      );

      return { previousQueries };
    },
    onError: (_err, _chatId, context) => {
      context?.previousQueries.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WorkerInbox.DIRECT_CHAT],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.WorkerInbox.INDICATORS],
      });
    },
  });
};
